/* La Pepa · Story-Viewer der Produktseite (Stories „Míralo en video“ und Sección „Videos“)
 * Zeigt nur die Videos des einen Produkts (custom.videos) nacheinander, mit Fortschrittsbalken oben wie bei Instagram.
 * Tippen rechts = nächstes Video, links = vorheriges, Halten = Pause, nach unten wischen / X / Esc / Zurück = schließen.
 * Nach dem letzten Video schließt der Viewer. Kein Wischen zu anderen Produkten (das bleibt dem Swipe Feed).
 *
 * window.lpStories.open({ item, startVideoId, surface, trigger })
 *   item: Produkt-JSON aus snippets/swipe-feed-item-json.liquid (vd = Videos, v = Varianten)
 * Messung wie im Swipe Feed (lpTrack): video_open, video_play, video_qualified_view, video_progress, video_view_end,
 * video_add_to_cart; Line-Item-Eigenschaften _origen: video-<surface>, _video: <id>.
 */
const SOUND_KEY = 'sf-sound';
const SEEN_KEY = 'lp-videos-vistos';
const SEEN_LIMIT = 200;
const QUALIFIED_MS = 3000;
const PROGRESS_MARKS = [25, 50, 75, 100];
const HOLD_MS = 220;
const PREV_ZONE = 0.3;

const read = (key, fallback) => { try { const v = JSON.parse(localStorage.getItem(key)); return v ?? fallback; } catch (e) { return fallback; } };
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* privat */ } };
const canHls = () => Boolean(document.createElement('video').canPlayType('application/vnd.apple.mpegurl'));
const slowNetwork = () => { const c = navigator.connection; return Boolean(c?.saveData || /2g|3g/.test(c?.effectiveType || '')); };
const icon = (path) => `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">${path}</svg>`;
const ICONS = {
  close: icon('<path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>'),
  soundOff: icon('<path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor"/><path d="m16 9 5 6m0-6-5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'),
  soundOn: icon('<path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor"/><path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>'),
};

const CSS = `
.lps{position:fixed;inset:0;z-index:2147483000;background:#000;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .18s ease;touch-action:none;-webkit-user-select:none;user-select:none}
.lps.is-open{opacity:1}
.lps__stage{position:relative;width:100%;height:100%;max-width:min(100vw,calc(100dvh * 9 / 16));background:#111;overflow:hidden}
@media (min-width:700px){.lps__stage{height:min(92dvh,900px);border-radius:16px}}
.lps__video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#111}
.lps__video[hidden]{display:none}
.lps__shade{position:absolute;inset:0;pointer-events:none;background:linear-gradient(to bottom,rgba(0,0,0,.45),transparent 22%,transparent 62%,rgba(0,0,0,.6))}
.lps__bars{position:absolute;top:10px;left:10px;right:10px;display:flex;gap:4px;z-index:2}
.lps__bar{flex:1;height:3px;border-radius:2px;background:rgba(255,255,255,.35);overflow:hidden}
.lps__bar span{display:block;height:100%;background:#fff;transform-origin:left;transform:scaleX(0)}
.lps__bar.is-done span{transform:scaleX(1)}
.lps__top{position:absolute;top:22px;left:12px;right:8px;display:flex;align-items:center;gap:8px;z-index:2;color:#fff}
.lps__title{flex:1;min-width:0;font-size:14px;font-weight:600;line-height:1.25;text-shadow:0 1px 2px rgba(0,0,0,.4);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.lps__icon{display:grid;place-items:center;width:40px;height:40px;padding:0;border:0;border-radius:50%;background:transparent;color:#fff;cursor:pointer}
.lps__icon:focus-visible,.lps__buy:focus-visible{outline:2px solid #fff;outline-offset:2px}
.lps__bottom{position:absolute;left:0;right:0;bottom:0;padding:14px 14px calc(14px + env(safe-area-inset-bottom));z-index:2;color:#fff}
.lps__caption{margin:0 0 12px;font-size:15px;font-weight:600;line-height:1.3;text-shadow:0 1px 2px rgba(0,0,0,.5)}
.lps__buy{position:relative;display:flex;width:100%;min-height:52px;align-items:center;justify-content:center;gap:8px;padding:0 18px;border:0;border-radius:999px;background:var(--lp-brand,#8e2f80);color:#fff;font:inherit;font-size:16px;font-weight:700;cursor:pointer}
.lps__buy[disabled]{opacity:.6;cursor:default}
.lps__price{font-weight:600;opacity:.9}
.lps__spinner{position:absolute;top:50%;left:50%;width:34px;height:34px;margin:-17px 0 0 -17px;border:3px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:lps-spin .8s linear infinite;z-index:1;display:none}
.lps.is-waiting .lps__spinner{display:block}
@keyframes lps-spin{to{transform:rotate(360deg)}}
.lps__tap{position:absolute;top:70px;bottom:120px;left:0;right:0;z-index:1}
html.lps-locked,html.lps-locked body{overflow:hidden}
@media (prefers-reduced-motion:reduce){.lps{transition:none}}
`;

function injectStyle() {
  if (document.getElementById('lp-stories-css')) return;
  const style = document.createElement('style');
  style.id = 'lp-stories-css';
  style.textContent = CSS;
  document.head.append(style);
}

function selectedVariant(item) {
  // Variante, die auf der Produktseite gewählt ist; sonst die erste verfügbare.
  const ids = new Set(item.v.map((v) => String(v.id)));
  for (const input of document.querySelectorAll('form[is="product-form"] [name="id"], form[action*="/cart/add"] [name="id"]')) {
    if (ids.has(String(input.value))) return item.v.find((v) => String(v.id) === String(input.value));
  }
  return item.v.find((v) => v.av) || item.v[0];
}

class Stories {
  open({ item, startVideoId, surface = 'historias', trigger } = {}) {
    if (this.el || !item?.vd?.length) return;
    injectStyle();
    this.item = item;
    this.videos = item.vd;
    this.surface = surface;
    this.trigger = trigger || document.activeElement;
    this.index = Math.max(0, this.videos.findIndex((v) => String(v.id) === String(startVideoId)));
    this.muted = read(SOUND_KEY, false) !== true;
    this.stats = new Map();
    this.players = new Map();
    this.hls = canHls();
    this.slow = slowNetwork();
    this.build();
    this.show(this.index, true);
  }

  build() {
    const el = document.createElement('div');
    el.className = 'lps';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', this.item.t);
    el.innerHTML = `
      <div class="lps__stage">
        <div class="lps__shade"></div>
        <div class="lps__spinner" aria-hidden="true"></div>
        <div class="lps__bars">${this.videos.map(() => '<div class="lps__bar"><span></span></div>').join('')}</div>
        <div class="lps__top">
          <span class="lps__title"></span>
          <button type="button" class="lps__icon" data-lps-sound></button>
          <button type="button" class="lps__icon" data-lps-close aria-label="Cerrar">${ICONS.close}</button>
        </div>
        <div class="lps__tap" data-lps-tap></div>
        <div class="lps__bottom">
          <p class="lps__caption"></p>
          <button type="button" class="lps__buy" data-lps-buy></button>
        </div>
      </div>`;
    el.querySelector('.lps__title').textContent = this.item.t;
    this.el = el;
    this.stage = el.querySelector('.lps__stage');
    this.bars = [...el.querySelectorAll('.lps__bar')];
    this.renderSound();
    this.renderBuy();

    el.querySelector('[data-lps-close]').addEventListener('click', () => this.close('close'));
    el.querySelector('[data-lps-sound]').addEventListener('click', () => this.toggleSound());
    el.querySelector('[data-lps-buy]').addEventListener('click', (e) => this.addToCart(e.currentTarget));
    this.bindGestures(el.querySelector('[data-lps-tap]'));

    this.onKey = (e) => {
      if (e.key === 'Escape') this.close('close');
      else if (e.key === 'ArrowRight') this.next();
      else if (e.key === 'ArrowLeft') this.prev();
    };
    this.onPop = () => this.close('back');
    this.onVisibility = () => (document.hidden ? this.pause() : this.resume());
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('popstate', this.onPop);
    history.pushState({ lpStories: true }, '');

    document.body.append(el);
    document.documentElement.classList.add('lps-locked');
    requestAnimationFrame(() => el.classList.add('is-open'));
    el.querySelector('[data-lps-close]').focus({ preventScroll: true });
  }

  bindGestures(zone) {
    let down = null;
    zone.addEventListener('pointerdown', (e) => {
      down = { x: e.clientX, y: e.clientY, t: Date.now(), held: false };
      down.timer = setTimeout(() => { if (down) { down.held = true; this.pause(); } }, HOLD_MS);
    });
    const end = (e, cancelled) => {
      if (!down) return;
      clearTimeout(down.timer);
      const dy = e.clientY - down.y;
      const dx = e.clientX - down.x;
      const held = down.held;
      down = null;
      if (held) { this.resume(); }
      if (cancelled) return;
      if (dy > 80 && Math.abs(dy) > Math.abs(dx)) { this.close('swipe_down'); return; }
      if (held || Math.abs(dx) > 30 || Math.abs(dy) > 30) return;
      const rect = this.stage.getBoundingClientRect();
      if (e.clientX - rect.left < rect.width * PREV_ZONE) this.prev(); else this.next();
    };
    zone.addEventListener('pointerup', (e) => end(e, false));
    zone.addEventListener('pointercancel', (e) => end(e, true));
    zone.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // HLS (cdn.shopify.com, siehe swipe-feed-item-json) wo nativ möglich, sonst mp4; lädt eine Quelle nicht, springt player() zur nächsten.
  sources(video) {
    return [...new Set([this.hls && video.hls, this.slow ? video.s4 : video.s7, video.s4, video.hls].filter(Boolean))];
  }

  player(i) {
    if (this.players.has(i)) return this.players.get(i);
    const video = this.videos[i];
    const el = document.createElement('video');
    el.className = 'lps__video';
    el.hidden = true;
    el.playsInline = true;
    el.setAttribute('playsinline', '');
    el.muted = true;
    el.preload = 'auto';
    el.poster = video.p;
    const sources = this.sources(video);
    let source = 0;
    el.src = sources[0];
    el.addEventListener('error', () => {
      if (!el.getAttribute('src') || ++source >= sources.length) return;
      el.src = sources[source];
      if (i === this.index && !this.paused) this.play(el);
    });
    el.addEventListener('ended', () => { if (i === this.index) this.next(true); });
    el.addEventListener('waiting', () => { if (i === this.index) this.el?.classList.add('is-waiting'); });
    el.addEventListener('playing', () => { if (i === this.index) { this.el?.classList.remove('is-waiting'); this.onPlaying(i); } });
    el.addEventListener('timeupdate', () => { if (i === this.index) this.onTime(i); });
    this.stage.insertBefore(el, this.stage.firstChild);
    const p = { el, video };
    this.players.set(i, p);
    return p;
  }

  release(i) {
    const p = this.players.get(i);
    if (!p) return;
    p.el.pause();
    p.el.removeAttribute('src');
    p.el.load();
    p.el.remove();
    this.players.delete(i);
  }

  show(i, opening = false) {
    const prev = this.players.get(this.index);
    if (!opening) this.commitView('advance');
    if (prev && this.index !== i) { prev.el.pause(); prev.el.hidden = true; }
    this.index = i;
    this.bars.forEach((bar, n) => {
      bar.classList.toggle('is-done', n < i);
      bar.querySelector('span').style.transform = n < i ? 'scaleX(1)' : 'scaleX(0)';
    });
    this.el.querySelector('.lps__caption').textContent = this.videos[i].a || '';
    const p = this.player(i);
    p.el.hidden = false;
    p.el.muted = this.muted;
    try { p.el.currentTime = 0; } catch (e) { /* noch nicht geladen */ }
    this.view = { id: this.videos[i].id, index: i, watchMs: 0, percent: 0, last: 0, startedAt: Date.now() };
    this.el.classList.add('is-waiting');
    this.play(p.el);
    this.track('video_open', this.props(i));
    // nächstes Video vorladen, alles außer ±1 freigeben
    if (i + 1 < this.videos.length) this.player(i + 1);
    [...this.players.keys()].forEach((n) => { if (Math.abs(n - i) > 1) this.release(n); });
  }

  play(el) {
    const attempt = el.play();
    if (attempt?.catch) {
      attempt.catch(() => {
        // Autoplay mit Ton blockiert: stumm weiter
        if (!el.muted) { el.muted = true; this.muted = true; this.renderSound(); el.play().catch(() => {}); }
      });
    }
  }

  next(fromEnd = false) {
    if (this.index + 1 < this.videos.length) this.show(this.index + 1);
    else this.close(fromEnd ? 'ended' : 'tap_end');
  }

  prev() {
    if (this.index > 0) this.show(this.index - 1);
    else { const p = this.players.get(0); if (p) { p.el.currentTime = 0; this.play(p.el); } }
  }

  pause() { this.paused = true; this.players.get(this.index)?.el.pause(); }

  resume() {
    if (!this.el) return;
    this.paused = false;
    const p = this.players.get(this.index);
    if (p) this.play(p.el);
  }

  onPlaying(i) {
    const stats = this.statsFor(this.videos[i].id);
    if (!stats.played) {
      stats.played = true;
      this.track('video_play', { ...this.props(i), ttff_ms: Date.now() - this.view.startedAt });
    }
  }

  onTime(i) {
    const player = this.players.get(i);
    if (!player) return; // nach release() feuert noch ein timeupdate
    const { el } = player;
    const view = this.view;
    const duration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : this.videos[i].d / 1000;
    if (!duration || !view) return;
    this.bars[i].querySelector('span').style.transform = `scaleX(${Math.min(1, el.currentTime / duration)})`;
    const delta = el.currentTime - view.last;
    const percent = Math.min(100, (el.currentTime / duration) * 100);
    view.last = el.currentTime;
    view.percent = Math.max(view.percent, percent);
    const stats = this.statsFor(view.id);
    if (delta > 0 && delta < 1.5) {
      view.watchMs += delta * 1000;
      stats.watchMs += delta * 1000;
      if (!stats.qualified && stats.watchMs >= QUALIFIED_MS) {
        stats.qualified = true;
        this.rememberSeen(view.id);
        this.track('video_qualified_view', this.props(i));
      }
    }
    PROGRESS_MARKS.forEach((mark) => {
      if (percent < mark - 1 || stats.marks.has(mark)) return;
      stats.marks.add(mark);
      this.track('video_progress', { ...this.props(i), percent: mark });
    });
  }

  statsFor(id) {
    if (!this.stats.has(id)) this.stats.set(id, { marks: new Set(), watchMs: 0, played: false, qualified: false });
    return this.stats.get(id);
  }

  rememberSeen(id) {
    const seen = read(SEEN_KEY, []).map(String).filter((x) => x !== String(id));
    seen.push(String(id));
    write(SEEN_KEY, seen.slice(-SEEN_LIMIT));
    document.querySelectorAll(`[data-video-id="${id}"]`).forEach((b) => b.classList.add('is-seen'));
  }

  commitView(reason) {
    const view = this.view;
    if (!view) return;
    this.view = null;
    this.track('video_view_end', { ...this.props(view.index), watch_ms: Math.round(view.watchMs), percent: Math.round(view.percent), reason });
  }

  toggleSound() {
    this.muted = !this.muted;
    write(SOUND_KEY, !this.muted);
    const p = this.players.get(this.index);
    if (p) { p.el.muted = this.muted; if (!this.muted) this.play(p.el); }
    this.renderSound();
  }

  renderSound() {
    const btn = this.el.querySelector('[data-lps-sound]');
    btn.innerHTML = this.muted ? ICONS.soundOff : ICONS.soundOn;
    btn.setAttribute('aria-label', this.muted ? 'Activar sonido' : 'Silenciar');
  }

  renderBuy() {
    const btn = this.el.querySelector('[data-lps-buy]');
    const variant = selectedVariant(this.item);
    this.variant = variant;
    btn.disabled = !variant?.av;
    btn.innerHTML = '';
    const label = document.createElement('span');
    label.textContent = variant?.av ? 'Añadir al carrito' : 'Agotado';
    btn.append(label);
    if (variant?.av && variant.p) {
      const price = document.createElement('span');
      price.className = 'lps__price';
      price.textContent = `· ${variant.p}`;
      btn.append(price);
    }
  }

  async addToCart(button) {
    const variant = this.variant;
    if (!variant?.av || button.getAttribute('aria-busy') === 'true') return;
    const video = this.videos[this.index];
    const body = [{ id: variant.id, quantity: 1, properties: { _origen: `video-${this.surface}`, _video: String(video.id) } }];
    try {
      if (window.LpCart) {
        await window.LpCart.add({ body, button });
      } else {
        const res = await fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: body }) });
        if (!res.ok) throw new Error('add failed');
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
      this.track('video_add_to_cart', { ...this.props(this.index), variant_id: variant.id, price: variant.p });
    } catch (e) { /* LpCart zeigt den Fehler am Button */ }
  }

  props(i) {
    return { video_id: this.videos[i]?.id, product_id: this.item.id, position: i };
  }

  track(name, properties = {}) {
    try { window.lpTrack?.(name, { surface: this.surface, viewer: 'stories', ...properties }); } catch (e) { /* Messung darf nie stören */ }
  }

  close(reason) {
    if (!this.el) return;
    this.commitView(reason);
    const el = this.el;
    this.el = null;
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('popstate', this.onPop);
    if (reason !== 'back' && history.state?.lpStories) history.back();
    [...this.players.keys()].forEach((n) => this.release(n));
    el.classList.remove('is-open');
    document.documentElement.classList.remove('lps-locked');
    setTimeout(() => el.remove(), 200);
    this.trigger?.focus?.({ preventScroll: true });
  }
}

window.lpStories = window.lpStories || new Stories();
